import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import InfoList, {
  type InfoListRow,
} from '@/components/Approval/@components/InfoList';
import {
  APPROVAL_SECTION_GAP_LG,
  InfoSectionTitle,
} from '@/components/Approval/@components/InfoSection';
import {
  cardReissueReasonLabel,
  cardReportStatusLabel,
  formatCardNumber,
  formatLimitView,
  isCardLimitIncreaseType,
  parseCorporateCardRequestAttributes,
} from '@/utils/approval/corporateCardRequest';
import type { ContentExtraProps } from '../../types';
import { ContentSlotRoot } from '../styles';

/** 206·207 법인카드 한도증액·재발급 — 레거시 조회처럼 저장값으로 다시 그리고, 값이 없으면 숨김 */
const CorporateCardRequestSlot = ({ data }: ContentExtraProps) => {
  const { t } = useTranslation();
  const saved = parseCorporateCardRequestAttributes(data.attributes);
  const isLimit = isCardLimitIncreaseType(data.apvType);
  if (!saved) return null;
  if (isLimit && (!saved.startDate || !saved.endDate)) return null;

  const cardNumber = `${formatCardNumber(saved.cardNumber)}${
    saved.cardNumberType === 'direct' ? ` (${t('직접 입력')})` : ''
  }`;

  const requestRows: InfoListRow[] = isLimit
    ? [
        { label: t('카드 번호'), value: cardNumber },
        {
          label: t('사용 기간'),
          value: `${saved.startDate} ~ ${saved.endDate}`,
        },
        {
          label: t('금액 한도'),
          value: `${t('기본 한도')}: ${formatLimitView(saved.currentLimit)}${t(
            '만원',
          )} → ${t('최종 한도')}: ${formatLimitView(saved.requestedLimit)}${t(
            '만원',
          )}`,
        },
        {
          label: t('사유'),
          value: <Box sx={{ whiteSpace: 'pre-wrap' }}>{saved.reason}</Box>,
        },
      ]
    : [
        { label: t('카드 번호'), value: cardNumber },
        {
          label: t('재발급 사유'),
          value: `${t(cardReissueReasonLabel(saved.reason))}${
            saved.reason === 'other' && saved.reasonText
              ? ` (${t('사유')}: ${saved.reasonText})`
              : ''
          }`,
        },
        ...(saved.reason === 'lost'
          ? [
              {
                label: t('분실 신고 여부'),
                value: t(cardReportStatusLabel(saved.reportStatus)),
              },
            ]
          : []),
      ];

  return (
    <ContentSlotRoot>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: `${APPROVAL_SECTION_GAP_LG}px`,
        }}
      >
        <Box>
          <InfoSectionTitle sx={{ mb: 1 }}>{t('신청자 정보')}</InfoSectionTitle>
          <InfoList
            labelWidth={110}
            rows={[
              { label: t('이름'), value: saved.employeeName },
              { label: t('사번'), value: saved.employeeNumber },
              { label: t('소속'), value: saved.department },
              { label: t('입사일'), value: saved.hireDate },
            ]}
          />
        </Box>
        <Box>
          <InfoSectionTitle sx={{ mb: 1 }}>{t('신청 정보')}</InfoSectionTitle>
          <InfoList labelWidth={110} rows={requestRows} />
        </Box>
      </Box>
    </ContentSlotRoot>
  );
};

export default CorporateCardRequestSlot;
